import { useState, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronLeft, Sparkles, CreditCard, Calendar, Moon, Sun, Ticket, ChevronRight, Shield, ScrollText, MessageSquarePlus, X, Send, Cake, Pencil, Trophy, Flame, Gift, Copy, Check, Users, Share2 } from 'lucide-react'
import { liff } from '../lib/liff'
import { STREAK_BADGES } from './HomePage'
import { useNavigate } from 'react-router-dom'
import { useAppStore } from '../store/useAppStore'
import { api } from '../lib/api'

interface DreamItem {
  id: string
  animal: string
  feeling: string
  context: string
  lucky_numbers: number[]
  lucky_numbers_3d?: number[]
  gemini_response?: string
  interpretation?: string
  created_at: string
}

interface UserStats {
  total_dreams: number
  total_payments: number
  member_since: string | null
  days_member: number
  recent_dreams: DreamItem[]
  recent_transactions: {
    ref_code: string
    amount_baht: number
    status: string
    created_at: string
    completed_at: string | null
  }[]
}

const ANIMAL_LABELS: Record<string, string> = {
  snake: '\u{1F40D} \u0E07\u0E39',
  fish: '\u{1F41F} \u0E1B\u0E25\u0E32',
  elephant: '\u{1F418} \u0E0A\u0E49\u0E32\u0E07',
  tiger: '\u{1F42F} \u0E40\u0E2A\u0E37\u0E2D',
  bird: '\u{1F426} \u0E19\u0E01',
  dog: '\u{1F415} \u0E2A\u0E38\u0E19\u0E31\u0E02',
  cat: '\u{1F408} \u0E41\u0E21\u0E27',
  human: '\u{1F464} \u0E04\u0E19',
  spirit: '\u{1F47B} \u0E27\u0E34\u0E0D\u0E0D\u0E32\u0E13',
  object: '\u{1F4E6} \u0E2A\u0E34\u0E48\u0E07\u0E02\u0E2D\u0E07',
  place: '\u{1F3DB} \u0E2A\u0E16\u0E32\u0E19\u0E17\u0E35\u0E48',
  other: '\u2728 \u0E2D\u0E37\u0E48\u0E19\u0E46',
}

const FEELING_LABELS: Record<string, string> = {
  fear: '\u0E01\u0E25\u0E31\u0E27',
  happy: '\u0E14\u0E35\u0E43\u0E08',
  confused: '\u0E2A\u0E31\u0E1A\u0E2A\u0E19',
  shocked: '\u0E15\u0E01\u0E43\u0E08',
  neutral: '\u0E40\u0E09\u0E22\u0E46',
  angry: '\u0E42\u0E01\u0E23\u0E18',
  sad: '\u0E40\u0E28\u0E23\u0E49\u0E32',
}

const CONTEXT_LABELS: Record<string, string> = {
  home: '\u0E17\u0E35\u0E48\u0E1A\u0E49\u0E32\u0E19',
  forest: '\u0E1B\u0E48\u0E32',
  water: '\u0E19\u0E49\u0E33/\u0E17\u0E30\u0E40\u0E25',
  temple: '\u0E27\u0E31\u0E14',
  still: '\u0E2D\u0E22\u0E39\u0E48\u0E19\u0E34\u0E48\u0E07\u0E46',
  chasing: '\u0E27\u0E34\u0E48\u0E07\u0E44\u0E25\u0E48',
  fighting: '\u0E15\u0E48\u0E2D\u0E2A\u0E39\u0E49',
  falling: '\u0E15\u0E01\u0E25\u0E07\u0E21\u0E32',
  lost: '\u0E2B\u0E25\u0E07\u0E17\u0E32\u0E07',
  flying: '\u0E1A\u0E34\u0E19',
}

function formatDate(s: string): string {
  const d = new Date(s)
  const months = ['\u0E21.\u0E04.','\u0E01.\u0E1E.','\u0E21\u0E35.\u0E04.','\u0E40\u0E21.\u0E22.','\u0E1E.\u0E04.','\u0E21\u0E34.\u0E22.','\u0E01.\u0E04.','\u0E2A.\u0E04.','\u0E01.\u0E22.','\u0E15.\u0E04.','\u0E1E.\u0E22.','\u0E18.\u0E04.']
  return `${d.getDate()} ${months[d.getMonth()]} ${(d.getFullYear() + 543).toString().slice(-2)}`
}

function formatTime(s: string): string {
  const d = new Date(s)
  return `${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`
}

function StatBox({ icon, label, value, unit, highlight, onClick }: {
  icon: React.ReactNode; label: string; value: number; unit: string; highlight?: boolean; onClick?: () => void
}) {
  const className = `p-3 rounded-2xl flex flex-col items-center text-center transition-transform ${onClick ? 'active:scale-95 cursor-pointer' : ''} ${highlight ? 'card-premium' : 'card-amulet'}`
  const inner = (
    <>
      <div style={{ color: highlight ? 'var(--color-gold-dark)' : 'var(--color-text-muted)' }}>{icon}</div>
      <div className="text-lg font-bold mt-1" style={{ color: 'var(--color-text)' }}>{value}</div>
      <div className="text-[10px]" style={{ color: 'var(--color-text-soft)' }}>{label} ({unit})</div>
    </>
  )
  return onClick ? (
    <button type="button" onClick={onClick} className={className}>{inner}</button>
  ) : (
    <div className={className}>{inner}</div>
  )
}

function SectionHeader({ title, icon }: { title: string; icon: string }) {
  return (
    <div className="px-4 mb-2 flex items-center gap-2">
      <span className="text-base">{icon}</span>
      <h2 className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--color-gold-dark)' }}>
        {title}
      </h2>
    </div>
  )
}

function EmptyHint({ text }: { text: string }) {
  return <div className="text-center py-4 text-xs" style={{ color: 'var(--color-text-soft)' }}>{text}</div>
}

function ReferralSection({ lineUid }: { lineUid: string }) {
  const queryClient = useQueryClient()
  const [copied, setCopied] = useState(false)
  const [sharing, setSharing] = useState(false)
  const [claiming, setClaiming] = useState(false)
  const [toast, setToast] = useState<string | null>(null)
  const [refInput, setRefInput] = useState('')
  const [applying, setApplying] = useState(false)

  const { data: ref } = useQuery({
    queryKey: ['referral', 'status', lineUid],
    queryFn: () => api.referral.status(lineUid),
    enabled: !!lineUid,
    staleTime: 1000 * 30,
  })

  if (!ref) {
    return (
      <div className="mx-4 mb-4 card-amulet p-4 animate-pulse">
        <div className="h-4 w-1/2 mb-2 rounded" style={{ background: 'var(--color-surface-2)' }} />
        <div className="h-3 w-2/3 rounded" style={{ background: 'var(--color-surface-2)' }} />
      </div>
    )
  }

  const liffUrl = `https://liff.line.me/2009976171-SBRuplHE?ref=${ref.code}`
  const totalSlots = ref.threshold
  const progress = ref.progress_to_next
  const dots = Array.from({ length: totalSlots }, (_, i) => i < progress)

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(liffUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setToast('ก๊อปปี้ไม่สำเร็จ')
      setTimeout(() => setToast(null), 2000)
    }
  }

  async function shareToLine() {
    if (sharing || !ref) return
    setSharing(true)
    try {
      const result = await liff.shareTargetPicker(
        [{
          type: 'flex',
          altText: '🔮 ลองแอป Amulet Digit — ทำนายฝัน + เลขเด็ดสายมู',
          contents: {
            type: 'bubble',
            hero: {
              type: 'image',
              url: 'https://www.amuletdigit.com/logo.png',
              size: 'full',
              aspectRatio: '1.91:1',
              aspectMode: 'cover',
              backgroundColor: '#f5f1e8',
            },
            body: {
              type: 'box', layout: 'vertical',
              backgroundColor: '#1c1810', paddingAll: '16px', spacing: 'sm',
              contents: [
                { type: 'text', text: 'Amulet Digit', color: '#b8945a', size: 'lg', weight: 'bold' },
                { type: 'text', text: '🔮 ทำนายฝัน + เลขเด็ดสายมู', color: '#f0ead8', size: 'sm', wrap: true, margin: 'sm' },
                { type: 'separator', margin: 'md', color: '#2d2215' },
                { type: 'text', text: '🎁 เปิดใช้ฟรี รับ 3 ตั๋วทันที', color: '#d4b884', size: 'xs', weight: 'bold', margin: 'md' },
                { type: 'text', text: `โค้ดเพื่อนชวน: ${ref.code}`, color: '#b8b09a', size: 'xs', margin: 'sm' },
              ],
            },
            footer: {
              type: 'box', layout: 'vertical',
              backgroundColor: '#16120d', paddingAll: '12px',
              contents: [{
                type: 'button',
                action: { type: 'uri', label: 'เปิดแอปเลย ✨', uri: liffUrl },
                style: 'primary', color: '#b8945a', height: 'sm',
              }],
            },
          },
        }],
        { isMultiple: true }
      )
      if (result?.status === 'success') {
        setToast('ส่งให้เพื่อนแล้ว ✨')
        setTimeout(() => setToast(null), 2500)
      }
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e)
      if (e instanceof Error && e.name === 'AbortError') return
      if (msg.includes('not supported') || msg.includes('permission') || msg.includes('shareTargetPicker')) {
        // fallback → copy link instead
        await copyLink()
        setToast('ก๊อปปี้ลิงก์แล้ว — เอาไปแชร์ในแชทได้เลย')
      } else {
        setToast(`LINE: ${msg.slice(0, 60)}`)
      }
      setTimeout(() => setToast(null), 3000)
    } finally {
      setSharing(false)
    }
  }

  async function applyManualCode() {
    if (applying || !ref) return
    const code = refInput.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8)
    if (code.length < 4) {
      setToast('โค้ดควรยาว 4-8 ตัวอักษร')
      setTimeout(() => setToast(null), 2500)
      return
    }
    setApplying(true)
    try {
      const res = await api.referral.apply(lineUid, code)
      setToast(`🎉 ใช้โค้ดของ ${res.referrer_name} แล้ว!`)
      setRefInput('')
      await queryClient.invalidateQueries({ queryKey: ['referral', 'status', lineUid] })
    } catch (err) {
      const errCode = (err as Error & { code?: string }).code
      const msg = errCode === 'already_referred' ? 'คุณใช้โค้ดเพื่อนชวนไปแล้ว'
                : errCode === 'self_referral'    ? 'ใช้โค้ดตัวเองไม่ได้'
                : errCode === 'invalid_code'     ? 'ไม่พบโค้ดนี้'
                : 'บันทึกไม่สำเร็จ'
      setToast(msg)
    } finally {
      setApplying(false)
      setTimeout(() => setToast(null), 3000)
    }
  }

  async function claimRewards() {
    if (claiming || !ref || ref.rewards_available <= 0) return
    setClaiming(true)
    try {
      const res = await api.referral.claim(lineUid)
      setToast(`🎉 รับ ${res.tickets_granted} ตั๋วฟรี!`)
      setTimeout(() => setToast(null), 3000)
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['referral', 'status', lineUid] }),
        queryClient.invalidateQueries({ queryKey: ['user', 'profile', lineUid] }),
      ])
    } catch (err) {
      setToast((err as Error).message)
      setTimeout(() => setToast(null), 2500)
    } finally {
      setClaiming(false)
    }
  }

  return (
    <>
      <SectionHeader title={'ชวนเพื่อน 🎁'} icon={'\u{1F381}'} />
      <div className="mx-4 mb-4 card-premium p-4">
        {/* Headline */}
        <div className="flex items-start gap-3 mb-3">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
               style={{ background: 'var(--gradient-gold)' }}>
            <Gift size={22} color="#fff" />
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-sm" style={{ color: 'var(--color-text)' }}>
              ชวนเพื่อน {totalSlots} คน รับฟรี 1 ตั๋ว
            </h3>
            <p className="text-[11px] mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
              เพื่อนเปิดแอปครั้งแรกถือว่าครบ — ไม่มีจำกัดจำนวน
            </p>
          </div>
        </div>

        {/* Code + copy */}
        <div className="flex items-center gap-2 mb-3 p-3 rounded-xl"
             style={{ background: 'var(--color-surface-2)', border: '1px dashed var(--color-gold)' }}>
          <span className="text-[10px] font-semibold" style={{ color: 'var(--color-text-soft)' }}>
            โค้ดของคุณ
          </span>
          <span className="font-mono font-bold text-base tracking-wider flex-1"
                style={{ color: 'var(--color-gold-dark)' }}>
            {ref.code}
          </span>
          <button onClick={copyLink}
                  className="px-2.5 py-1.5 rounded-lg flex items-center gap-1 text-[11px] font-semibold transition-transform active:scale-95"
                  style={{ background: 'var(--color-surface)', color: 'var(--color-gold-dark)' }}>
            {copied ? <Check size={12} /> : <Copy size={12} />}
            {copied ? 'ก๊อปปี้แล้ว' : 'ก๊อปปี้ลิงก์'}
          </button>
        </div>

        {/* Progress dots */}
        <div className="flex items-center gap-1.5 mb-3">
          {dots.map((filled, i) => (
            <div key={i} className="flex-1 h-2 rounded-full"
                 style={{ background: filled ? 'var(--color-gold)' : 'var(--color-surface-3)' }} />
          ))}
          <span className="text-[11px] ml-2 font-semibold" style={{ color: 'var(--color-text-muted)' }}>
            {progress}/{totalSlots}
          </span>
        </div>
        <div className="text-[11px] mb-3" style={{ color: 'var(--color-text-soft)' }}>
          <Users size={11} className="inline mr-1" style={{ verticalAlign: '-1px' }} />
          ชวนแล้ว {ref.total_invited} คน — เปิดแอปจริง {ref.qualified_count} คน
          {ref.rewards_claimed > 0 && ` · รับไปแล้ว ${ref.rewards_claimed} ตั๋ว`}
        </div>

        {/* Buttons */}
        <div className="flex gap-2">
          <button onClick={shareToLine} disabled={sharing}
                  className="flex-1 py-3 rounded-xl font-semibold text-sm flex items-center justify-center gap-2 btn-gold shimmer disabled:opacity-50">
            <Share2 size={15} />
            {sharing ? 'กำลังเปิด...' : 'ชวนผ่าน LINE'}
          </button>
          {ref.rewards_available > 0 && (
            <button onClick={claimRewards} disabled={claiming}
                    className="px-4 py-3 rounded-xl font-semibold text-sm flex items-center gap-1.5 transition-transform active:scale-95 disabled:opacity-50"
                    style={{ background: 'var(--gradient-sage)', color: '#fff', boxShadow: '0 4px 12px rgba(122,138,106,0.3)' }}>
            <Gift size={14} />
            {claiming ? '...' : `รับ ${ref.rewards_available} ตั๋ว`}
          </button>
          )}
        </div>

        {/* Invited list */}
        {ref.invited_friends.length > 0 && (
          <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--color-surface-3)' }}>
            <div className="text-[10px] mb-2 font-semibold" style={{ color: 'var(--color-text-soft)' }}>
              เพื่อนที่ชวน
            </div>
            <div className="flex flex-col gap-1.5 max-h-32 overflow-y-auto">
              {ref.invited_friends.slice(0, 8).map((f, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span style={{ color: f.qualified ? 'var(--color-gold-dark)' : 'var(--color-text-soft)' }}>
                    {f.qualified ? '✓' : '○'}
                  </span>
                  <span className="flex-1 truncate" style={{ color: 'var(--color-text)' }}>
                    {f.display_name ?? 'ผู้ใช้ใหม่'}
                  </span>
                  <span className="text-[10px]" style={{ color: 'var(--color-text-soft)' }}>
                    {f.qualified ? 'พร้อมนับ' : 'ยังไม่เปิด'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Referred by (if any) */}
        {ref.referred_by && (
          <div className="mt-3 pt-3 border-t text-[10px]"
               style={{ borderColor: 'var(--color-surface-3)', color: 'var(--color-text-soft)' }}>
            🙏 ขอบคุณ {ref.referred_by.display_name ?? 'เพื่อน'} ที่ชวนคุณมา
          </div>
        )}

        {/* Manual ref input — แสดงเฉพาะคนยังไม่มีคนชวน */}
        {!ref.referred_by && (
          <div className="mt-3 pt-3 border-t" style={{ borderColor: 'var(--color-surface-3)' }}>
            <div className="text-[10px] mb-2 font-semibold flex items-center gap-1.5" style={{ color: 'var(--color-text-soft)' }}>
              <Gift size={11} />
              {'ได้รับโค้ดจากเพื่อน?'}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={refInput}
                onChange={e => setRefInput(e.target.value)}
                placeholder="ใส่โค้ดเพื่อน"
                inputMode="text"
                autoCapitalize="characters"
                maxLength={8}
                className="flex-1 px-3 py-2 rounded-lg text-sm font-mono tracking-widest outline-none"
                style={{
                  background: 'var(--color-surface-2)',
                  color: 'var(--color-text)',
                  border: '1px solid var(--color-surface-3)',
                }}
              />
              <button onClick={applyManualCode} disabled={applying || refInput.length < 4}
                      className="px-3 py-2 rounded-lg text-xs font-semibold transition-transform active:scale-95 disabled:opacity-40"
                      style={{ background: 'var(--gradient-gold)', color: '#fff' }}>
                {applying ? '...' : 'ใช้โค้ด'}
              </button>
            </div>
          </div>
        )}
      </div>
      {toast && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-xl text-sm font-semibold"
             style={{ background: 'var(--gradient-gold)', color: '#fff', boxShadow: '0 4px 16px rgba(184,148,90,0.4)' }}>
          {toast}
        </div>
      )}
    </>
  )
}

function ComingSoonCard({ icon, label, desc }: { icon: React.ReactNode; label: string; desc: string }) {
  return (
    <div className="p-3 rounded-2xl card-amulet opacity-60">
      <div className="flex items-center gap-2">
        <div style={{ color: 'var(--color-gold-dark)' }}>{icon}</div>
        <div className="text-sm font-semibold" style={{ color: 'var(--color-text)' }}>{label}</div>
      </div>
      <div className="text-[10px] mt-1" style={{ color: 'var(--color-text-soft)' }}>{desc}</div>
      <div className="text-[9px] mt-1.5 inline-block px-1.5 py-0.5 rounded"
           style={{ background: 'var(--color-surface-2)', color: 'var(--color-text-muted)' }}>
        {'\u0E40\u0E23\u0E47\u0E27\u0E46 \u0E19\u0E35\u0E49'}
      </div>
    </div>
  )
}

const FEEDBACK_TYPES = [
  { value: 'suggestion', label: '\u{1F4A1} \u0E02\u0E49\u0E2D\u0E40\u0E2A\u0E19\u0E2D\u0E41\u0E19\u0E30' },
  { value: 'bug',        label: '\u{1F41B} \u0E23\u0E32\u0E22\u0E07\u0E32\u0E19\u0E1B\u0E31\u0E0D\u0E2B\u0E32' },
  { value: 'compliment', label: '\u2764\uFE0F \u0E0A\u0E21\u0E40\u0E0A\u0E22' },
  { value: 'other',      label: '\u{1F4AC} \u0E2D\u0E37\u0E48\u0E19\u0E46' },
]

function FeedbackModal({ onClose, displayName }: { onClose: () => void; displayName: string }) {
  const [type, setType] = useState('suggestion')
  const [text, setText] = useState('')
  const [sent, setSent] = useState(false)
  const [loading, setLoading] = useState(false)

  async function handleSend() {
    if (!text.trim()) return
    setLoading(true)
    const typeLabel = FEEDBACK_TYPES.find(t => t.value === type)?.label ?? type
    const msg = encodeURIComponent(`[Feedback: ${typeLabel}]\n\u0E08\u0E32\u0E01: ${displayName}\n\n${text.trim()}`)
    window.open(`https://line.me/R/ti/p/@538rqskd?text=${msg}`, '_blank')
    setLoading(false)
    setSent(true)
  }

  if (sent) {
    return (
      <div className="fixed inset-0 z-50 flex items-end justify-center p-4"
           style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
        <div className="w-full max-w-md rounded-3xl p-8 text-center mb-4"
             style={{ background: 'var(--color-surface)' }} onClick={e => e.stopPropagation()}>
          <div className="text-5xl mb-3">{'\u{1F64F}'}</div>
          <h3 className="text-base font-bold mb-2" style={{ color: 'var(--color-text)' }}>
            {'\u0E02\u0E2D\u0E1A\u0E04\u0E38\u0E13\u0E21\u0E32\u0E01\u0E04\u0E23\u0E31\u0E1A!'}
          </h3>
          <p className="text-sm mb-5" style={{ color: 'var(--color-text-muted)' }}>
            {'Feedback \u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13\u0E21\u0E35\u0E04\u0E48\u0E32\u0E21\u0E32\u0E01\u0E2A\u0E33\u0E2B\u0E23\u0E31\u0E1A\u0E40\u0E23\u0E32'}
          </p>
          <button onClick={onClose} className="w-full py-3 rounded-xl font-semibold btn-gold">
            {'\u0E1B\u0E34\u0E14'}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4"
         style={{ background: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div className="w-full max-w-md rounded-3xl pb-6 mb-2"
           style={{ background: 'var(--color-surface)' }} onClick={e => e.stopPropagation()}>
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 rounded-full" style={{ background: 'var(--color-surface-3)' }} />
        </div>
        <div className="flex items-center justify-between px-5 py-3">
          <div className="flex items-center gap-2">
            <MessageSquarePlus size={18} style={{ color: 'var(--color-gold-dark)' }} />
            <h3 className="text-sm font-bold" style={{ color: 'var(--color-text)' }}>
              {'\u0E2A\u0E48\u0E07 Feedback'}
            </h3>
          </div>
          <button onClick={onClose} style={{ color: 'var(--color-text-muted)' }}><X size={20} /></button>
        </div>
        <div className="px-5 flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-2">
            {FEEDBACK_TYPES.map(t => (
              <button key={t.value} onClick={() => setType(t.value)}
                      className="py-2.5 px-3 rounded-xl text-xs font-semibold transition-all"
                      style={{
                        background: type === t.value ? 'var(--gradient-gold)' : 'var(--color-surface-2)',
                        color: type === t.value ? '#fff' : 'var(--color-text-muted)',
                      }}>
                {t.label}
              </button>
            ))}
          </div>
          <textarea value={text} onChange={e => setText(e.target.value)}
                    placeholder={'\u0E1E\u0E34\u0E21\u0E1E\u0E4C\u0E02\u0E49\u0E2D\u0E04\u0E27\u0E32\u0E21\u0E02\u0E2D\u0E07\u0E04\u0E38\u0E13\u0E17\u0E35\u0E48\u0E19\u0E35\u0E48...'}
                    rows={4} maxLength={500}
                    className="w-full rounded-2xl p-4 text-sm resize-none outline-none"
                    style={{ background: 'var(--color-surface-2)', color: 'var(--color-text)', border: '1px solid var(--color-surface-3)' }} />
          <div className="text-right text-[10px]" style={{ color: 'var(--color-text-soft)', marginTop: -12 }}>
            {text.length}/500
          </div>
          <button onClick={handleSend} disabled={!text.trim() || loading}
                  className="w-full py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 btn-gold shimmer disabled:opacity-40">
            <Send size={16} />
            {loading ? '\u0E01\u0E33\u0E25\u0E31\u0E07\u0E2A\u0E48\u0E07...' : '\u0E2A\u0E48\u0E07 Feedback'}
          </button>
          <p className="text-center text-[10px]" style={{ color: 'var(--color-text-soft)' }}>
            {'Feedback \u0E08\u0E30\u0E16\u0E39\u0E01\u0E2A\u0E48\u0E07\u0E1C\u0E48\u0E32\u0E19 LINE OA \u0E02\u0E2D\u0E07\u0E40\u0E23\u0E32'}
          </p>
        </div>
      </div>
    </div>
  )
}

function BirthDateEditModal({ initial, lineUid, onClose, onSaved }: {
  initial: string | null
  lineUid: string
  onClose: () => void
  onSaved: () => void
}) {
  const today = new Date().toISOString().slice(0, 10)
  const [date, setDate] = useState(initial ?? '')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(date) && date <= today && date >= '1920-01-01'

  async function handleSave() {
    if (!valid) return
    setLoading(true)
    setError(null)
    try {
      await api.user.setBirthDate(lineUid, date)
      onSaved()
      onClose()
    } catch (err) {
      setError((err as Error).message || 'บันทึกไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6"
         style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose}>
      <div className="w-full max-w-sm rounded-3xl p-6"
           style={{ background: 'var(--color-surface)' }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Cake size={18} style={{ color: 'var(--color-gold-dark)' }} />
            <h3 className="text-base font-bold" style={{ color: 'var(--color-text)' }}>
              {'แก้ไขวันเกิด'}
            </h3>
          </div>
          <button onClick={onClose} style={{ color: 'var(--color-text-muted)' }}><X size={20} /></button>
        </div>
        <p className="text-xs mb-3" style={{ color: 'var(--color-text-muted)' }}>
          {'ระบบจะใช้วันเกิดเพื่ออ่านดวงเฉพาะคุณ — แก้ไขแล้วดวงวันนี้จะ reset ใหม่'}
        </p>
        <input
          type="date"
          value={date}
          max={today}
          min="1920-01-01"
          onChange={e => setDate(e.target.value)}
          className="w-full px-4 py-3 rounded-2xl text-base outline-none mb-2"
          style={{
            background: 'var(--color-surface-2)',
            color: 'var(--color-text)',
            border: '1px solid var(--color-surface-3)',
          }}
        />
        {error && <p className="text-xs mb-2" style={{ color: '#e53e3e' }}>{error}</p>}
        <div className="flex gap-3 mt-4">
          <button onClick={onClose}
                  className="flex-1 py-3 rounded-xl font-semibold text-sm"
                  style={{ background: 'var(--color-surface-2)', color: 'var(--color-text-muted)' }}>
            {'ยกเลิก'}
          </button>
          <button onClick={handleSave} disabled={!valid || loading}
                  className="flex-1 py-3 rounded-xl font-semibold text-sm btn-gold shimmer disabled:opacity-40">
            {loading ? 'กำลังบันทึก...' : 'บันทึก'}
          </button>
        </div>
      </div>
    </div>
  )
}

function RewardLadderModal({ ownedBadges, onClose }: { ownedBadges: string[]; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center p-4"
         style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose}>
      <div className="w-full max-w-md rounded-3xl p-5 max-h-[85vh] overflow-y-auto"
           style={{
             background: 'var(--color-surface)',
             marginBottom: 'calc(76px + env(safe-area-inset-bottom, 0px))',
           }}
           onClick={e => e.stopPropagation()}>
        <div className="flex justify-center pt-1 pb-3">
          <div className="w-10 h-1 rounded-full" style={{ background: 'var(--color-surface-3)' }} />
        </div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold flex items-center gap-2" style={{ color: 'var(--color-text)' }}>
            <span>{'\u{1F525}'}</span>
            <span>{'รางวัล Streak'}</span>
          </h3>
          <button onClick={onClose} style={{ color: 'var(--color-text-muted)' }}><X size={20} /></button>
        </div>
        <p className="text-xs mb-4" style={{ color: 'var(--color-text-muted)' }}>
          {'เช็คอินทุกวันติดต่อกัน — ครบจำนวนวันรับรางวัลพิเศษ'}
        </p>
        <div className="flex flex-col gap-2">
          {STREAK_BADGES.map(b => {
            const owned = ownedBadges.includes(b.code)
            return (
              <div key={b.code}
                   className="flex items-center gap-3 p-3 rounded-2xl"
                   style={{
                     background: owned ? 'var(--color-surface-2)' : 'var(--color-surface)',
                     border: '1px solid ' + (owned ? 'var(--color-gold)' : 'var(--color-surface-3)'),
                   }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-3xl flex-shrink-0"
                     style={{
                       background: owned ? 'var(--gradient-gold)' : 'var(--color-surface-2)',
                       filter: owned ? 'none' : 'grayscale(1)',
                       opacity: owned ? 1 : 0.6,
                     }}>
                  {b.emoji}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm" style={{ color: 'var(--color-text)' }}>
                    {b.label}
                  </div>
                  <div className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>
                    {'เช็คอินครบ '}{b.threshold}{' วันติด'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold" style={{ color: 'var(--color-gold-dark)' }}>
                    {'\u{1F3AB} +'}{b.bonus}
                  </div>
                  <div className="text-[9px]" style={{ color: 'var(--color-text-soft)' }}>
                    {owned ? 'ปลดล็อกแล้ว' : 'ยังไม่ปลดล็อก'}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
        <div className="text-center text-[10px] mt-3" style={{ color: 'var(--color-text-soft)' }}>
          {'\u{1F451} ครบ 30 วัน → repeat ทุก 30 วัน รับ +5 ตั๋วใหม่'}
        </div>
        <button onClick={onClose}
                className="w-full mt-4 py-3 rounded-xl font-semibold btn-gold shimmer">
          {'เข้าใจแล้ว'}
        </button>
      </div>
    </div>
  )
}

function formatBirthDate(s: string): string {
  const d = new Date(s + 'T00:00:00')
  const months = ['ม.ค.','ก.พ.','มี.ค.','เม.ย.','พ.ค.','มิ.ย.','ก.ค.','ส.ค.','ก.ย.','ต.ค.','พ.ย.','ธ.ค.']
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear() + 543}`
}

function DreamDetailModal({ dream, onClose }: { dream: DreamItem; onClose: () => void }) {
  const interpretation = dream.gemini_response ?? dream.interpretation ?? '-'
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
         style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose}>
      <div className="w-full max-w-md rounded-3xl p-6 text-center max-h-[90vh] overflow-y-auto"
           style={{ background: 'var(--color-surface)' }} onClick={e => e.stopPropagation()}>
        <div className="text-4xl mb-2">{ANIMAL_LABELS[dream.animal]?.split(' ')[0] ?? '\u2728'}</div>
        <div className="text-xs mb-1" style={{ color: 'var(--color-text-muted)' }}>
          {formatDate(dream.created_at)} • {formatTime(dream.created_at)}
        </div>
        <h3 className="text-base font-bold mb-3" style={{ color: 'var(--color-gold-dark)' }}>
          {'\u0E1D\u0E31\u0E19\u0E40\u0E2B\u0E47\u0E19 '}{ANIMAL_LABELS[dream.animal] ?? dream.animal}
        </h3>
        <div className="flex items-center justify-center gap-2 text-[11px] mb-4">
          <span className="px-2 py-0.5 rounded-full" style={{ background: 'var(--color-surface-2)', color: 'var(--color-text-muted)' }}>
            {'\u{1F60A} '}{FEELING_LABELS[dream.feeling] ?? dream.feeling}
          </span>
          <span className="px-2 py-0.5 rounded-full" style={{ background: 'var(--color-surface-2)', color: 'var(--color-text-muted)' }}>
            {'\u{1F319} '}{CONTEXT_LABELS[dream.context] ?? dream.context}
          </span>
        </div>
        <div className="p-4 rounded-2xl mb-4 text-sm leading-relaxed text-left"
             style={{ background: 'var(--color-surface-2)', color: 'var(--color-text)' }}>
          {interpretation}
        </div>
        <p className="text-xs mb-2" style={{ color: 'var(--color-text-muted)' }}>{'\u{1F340} \u0E40\u0E25\u0E02\u0E21\u0E07\u0E04\u0E25'}</p>
        <div className="flex justify-center gap-2 mb-2">
          {(dream.lucky_numbers ?? []).map((n, i) => (
            <div key={`2-${i}`} className="w-12 h-12 rounded-xl flex items-center justify-center text-base font-bold"
                 style={{ background: 'var(--gradient-gold)', color: '#fff' }}>
              {n.toString().padStart(2, '0')}
            </div>
          ))}
        </div>
        {(dream.lucky_numbers_3d ?? []).length > 0 && (
          <div className="flex justify-center gap-2 mb-5 flex-wrap">
            {(dream.lucky_numbers_3d ?? []).map((n, i) => (
              <div key={`3-${i}`} className="px-3 h-12 min-w-[4rem] rounded-xl flex items-center justify-center text-sm font-bold"
                   style={{ background: 'var(--gradient-sage)', color: '#fff' }}>
                {n.toString().padStart(3, '0')}
              </div>
            ))}
          </div>
        )}
        <button onClick={onClose} className="w-full py-3 rounded-xl font-semibold btn-gold shimmer">
          {'\u0E1B\u0E34\u0E14'}
        </button>
      </div>
    </div>
  )
}

export default function ProfilePage() {
  const navigate = useNavigate()
  const { profile, lineUid, theme, toggleTheme } = useAppStore()
  const [selectedDream, setSelectedDream] = useState<DreamItem | null>(null)
  const [showFeedback, setShowFeedback] = useState(false)
  const [showBirthEdit, setShowBirthEdit] = useState(false)
  const [showRewardModal, setShowRewardModal] = useState(false)

  const queryClient = useQueryClient()

  // Invalidate on mount เพื่อ sync ตั๋วที่อาจเปลี่ยนจากหน้าอื่น
  useEffect(() => {
    if (!lineUid) return
    queryClient.invalidateQueries({ queryKey: ['user', 'profile', lineUid] })
    queryClient.invalidateQueries({ queryKey: ['user', 'stats', lineUid] })
  }, [lineUid, queryClient])

  const { data: liveProfile } = useQuery({
    queryKey: ['user', 'profile', lineUid],
    queryFn: () => api.user.profile(lineUid!),
    enabled: !!lineUid,
    staleTime: 0,
  })

  const { data: stats } = useQuery({
    queryKey: ['user', 'stats', lineUid],
    queryFn: () => api.user.stats(lineUid!) as Promise<UserStats>,
    enabled: !!lineUid,
    staleTime: 1000 * 60,
  })

  const { data: savedNumbers, refetch: refetchSaved } = useQuery({
    queryKey: ['number', 'saved', lineUid],
    queryFn: () => api.number.list(lineUid!) as Promise<{ id: string; number: string; created_at: string; expire_at: string | null }[]>,
    enabled: !!lineUid,
    staleTime: 0,
  })

  const { data: numberHits } = useQuery({
    queryKey: ['number', 'hits', lineUid],
    queryFn: () => api.number.hits(lineUid!),
    enabled: !!lineUid,
    staleTime: 1000 * 60,
  })

  async function handleUnsave(number: string) {
    if (!lineUid) return
    try {
      await api.number.unsave(lineUid, number)
      refetchSaved()
    } catch {}
  }

  const tickets = (liveProfile as { dream_quota?: { paid_tickets?: number } } | undefined)?.dream_quota?.paid_tickets ?? profile?.dream_quota?.paid_tickets ?? 0
  const birthDate = (liveProfile as { birth_date?: string | null } | undefined)?.birth_date ?? null

  const liveStreak = (liveProfile as { streak?: { days?: number; best?: number; badges?: string[] } } | undefined)?.streak
  const streakDays  = liveStreak?.days  ?? profile?.streak?.days  ?? 0
  const streakBest  = liveStreak?.best  ?? profile?.streak?.best  ?? 0
  const userBadges  = liveStreak?.badges ?? profile?.streak?.badges ?? []

  return (
    <div className="flex flex-col min-h-dvh pb-6" style={{ background: 'var(--color-bg)' }}>

      {selectedDream && <DreamDetailModal dream={selectedDream} onClose={() => setSelectedDream(null)} />}
      {showFeedback && (
        <FeedbackModal onClose={() => setShowFeedback(false)} displayName={profile?.display_name ?? 'User'} />
      )}
      {showBirthEdit && lineUid && (
        <BirthDateEditModal
          initial={birthDate}
          lineUid={lineUid}
          onClose={() => setShowBirthEdit(false)}
          onSaved={() => {
            queryClient.invalidateQueries({ queryKey: ['user', 'profile', lineUid] })
            queryClient.invalidateQueries({ queryKey: ['horoscope', 'today', lineUid] })
          }}
        />
      )}
      {showRewardModal && (
        <RewardLadderModal ownedBadges={userBadges} onClose={() => setShowRewardModal(false)} />
      )}

      {/* Feedback FAB */}
      <button onClick={() => setShowFeedback(true)}
              className="fixed bottom-24 right-4 z-40 w-14 h-14 rounded-full flex items-center justify-center transition-transform active:scale-90"
              style={{ background: 'var(--gradient-gold)', boxShadow: '0 4px 20px rgba(184,148,90,0.45)' }}>
        <MessageSquarePlus size={22} color="#fff" />
      </button>

      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-6 pb-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/')} style={{ color: 'var(--color-text-muted)' }}>
            <ChevronLeft size={24} />
          </button>
          <h1 className="text-lg font-bold" style={{ color: 'var(--color-text)' }}>
            {'\u0E42\u0E1B\u0E23\u0E44\u0E1F\u0E25\u0E4C'}
          </h1>
        </div>
        <button onClick={toggleTheme}
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ background: 'var(--color-surface)', border: '1px solid var(--color-surface-3)' }}>
          {theme === 'light'
            ? <Moon size={18} style={{ color: 'var(--color-gold-dark)' }} />
            : <Sun size={18} style={{ color: 'var(--color-gold)' }} />}
        </button>
      </div>

      {/* Profile Card */}
      <div className="mx-4 mb-4 p-5 card-amulet flex items-center gap-4">
        <div className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center"
             style={{ background: 'var(--gradient-gold)' }}>
          {profile?.avatar_url
            ? <img src={profile.avatar_url} alt="" className="w-full h-full object-cover" />
            : <span className="text-3xl">{'\u{1FAB6}'}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-base truncate" style={{ color: 'var(--color-text)' }}>
            {profile?.display_name ?? 'Guest'}
          </div>
          <div className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
            {'\u0E2A\u0E21\u0E32\u0E0A\u0E34\u0E01\u0E15\u0E31\u0E49\u0E07\u0E41\u0E15\u0E48 '}{stats?.member_since ? formatDate(stats.member_since) : '\u2014'}
          </div>
          <div className="inline-flex items-center gap-1 mt-2 px-2.5 py-0.5 rounded-full text-[10px] font-semibold"
               style={{ background: 'var(--gradient-gold)', color: '#fff' }}>
            {'\u2B50 Free Tier'}
          </div>
        </div>
      </div>

      {/* Birth Date */}
      <div className="mx-4 mb-4">
        <button onClick={() => setShowBirthEdit(true)}
                className="w-full p-4 card-amulet flex items-center gap-3 text-left transition-transform active:scale-[0.98]">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--color-surface-2)' }}>
            <Cake size={18} style={{ color: 'var(--color-gold-dark)' }} />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-sm" style={{ color: 'var(--color-text)' }}>
              {'วันเกิด'}
            </div>
            <div className="text-[11px]" style={{ color: birthDate ? 'var(--color-text-muted)' : '#e53e3e' }}>
              {birthDate ? formatBirthDate(birthDate) : 'ยังไม่ได้ตั้ง — กดเพื่อใส่วันเกิด'}
            </div>
          </div>
          <Pencil size={14} style={{ color: 'var(--color-text-soft)' }} />
        </button>
      </div>

      {/* Stats */}
      <div className="mx-4 mb-4 grid grid-cols-3 gap-2">
        <StatBox icon={<Sparkles size={18} />} label={'\u0E17\u0E33\u0E19\u0E32\u0E22\u0E1D\u0E31\u0E19'} value={stats?.total_dreams ?? 0} unit={'\u0E04\u0E23\u0E31\u0E49\u0E07'} />
        <StatBox icon={<Ticket size={18} />} label={'\u0E15\u0E31\u0E4B\u0E27\u0E04\u0E07\u0E40\u0E2B\u0E25\u0E37\u0E2D'} value={tickets} unit={'\u0E43\u0E1A'} highlight onClick={() => navigate('/store')} />
        <StatBox icon={<Calendar size={18} />} label={'\u0E40\u0E1B\u0E47\u0E19\u0E2A\u0E21\u0E32\u0E0A\u0E34\u0E01'} value={stats?.days_member ?? 0} unit={'\u0E27\u0E31\u0E19'} />
      </div>

      {/* Streak Section \uD83D\uDD25 */}
      <SectionHeader title={'STREAK'} icon={'\u{1F525}'} />
      <div className="mx-4 mb-4 card-amulet p-4">
        <div className="grid grid-cols-2 gap-2 mb-3">
          <div className="p-3 rounded-xl text-center"
               style={{ background: 'var(--gradient-gold)', color: '#fff' }}>
            <Flame size={18} className="mx-auto mb-1" fill="#fff" />
            <div className="text-2xl font-bold leading-none">{streakDays}</div>
            <div className="text-[10px] mt-1 opacity-90 leading-tight">{'\u{1F525} \u0E40\u0E0A\u0E47\u0E04\u0E2D\u0E34\u0E19\u0E15\u0E34\u0E14\u0E15\u0E48\u0E2D\u0E01\u0E31\u0E19'}</div>
          </div>
          <div className="p-3 rounded-xl text-center"
               style={{ background: 'var(--color-surface-2)' }}>
            <Trophy size={18} className="mx-auto mb-1" style={{ color: 'var(--color-gold-dark)' }} />
            <div className="text-2xl font-bold leading-none" style={{ color: 'var(--color-text)' }}>{streakBest}</div>
            <div className="text-[10px] mt-1 leading-tight" style={{ color: 'var(--color-text-soft)' }}>
              {'\u0E40\u0E0A\u0E47\u0E04\u0E2D\u0E34\u0E19\u0E2A\u0E39\u0E07\u0E2A\u0E38\u0E14\u0E15\u0E34\u0E14\u0E15\u0E48\u0E2D\u0E01\u0E31\u0E19'}
            </div>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-2">
          {STREAK_BADGES.map(b => {
            const owned = userBadges.includes(b.code)
            return (
              <button key={b.code}
                      type="button"
                      onClick={() => setShowRewardModal(true)}
                      className="p-2 rounded-xl text-center transition-transform active:scale-95"
                      style={{
                        background: owned ? 'var(--gradient-gold)' : 'var(--color-surface-2)',
                        opacity: owned ? 1 : 0.5,
                      }}>
                <div className="text-2xl mb-1" style={{ filter: owned ? 'none' : 'grayscale(1)' }}>
                  {b.emoji}
                </div>
                <div className="text-[9px] font-semibold"
                     style={{ color: owned ? '#fff' : 'var(--color-text-soft)' }}>
                  {b.threshold}{'\u0E27\u0E31\u0E19'}
                </div>
              </button>
            )
          })}
        </div>
        <div className="text-center text-[10px] mt-2" style={{ color: 'var(--color-text-soft)' }}>
          {'\u0E41\u0E15\u0E30\u0E23\u0E32\u0E07\u0E27\u0E31\u0E25\u0E40\u0E1E\u0E37\u0E48\u0E2D\u0E14\u0E39\u0E23\u0E32\u0E22\u0E25\u0E30\u0E40\u0E2D\u0E35\u0E22\u0E14'}
        </div>
      </div>

{/* Number Hits \uD83C\uDFAF \u2014 \u0E41\u0E2A\u0E14\u0E07\u0E40\u0E09\u0E1E\u0E32\u0E30\u0E16\u0E49\u0E32\u0E21\u0E35\u0E1B\u0E23\u0E30\u0E27\u0E31\u0E15\u0E34\u0E16\u0E39\u0E01 */}
      {numberHits && numberHits.length > 0 && (
        <>
          <SectionHeader title={'\u0E40\u0E25\u0E02\u0E2E\u0E34\u0E15 \u{1F3AF}'} icon={'\u{1F3C6}'} />
          <div className="mx-4 mb-4 card-premium p-4 flex flex-col gap-2">
            {numberHits.slice(0, 5).map(h => (
              <div key={h.id} className="flex items-center gap-3 p-2 rounded-xl"
                   style={{ background: 'var(--color-surface-2)' }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center font-mono font-bold text-lg text-white"
                     style={{ background: 'var(--gradient-gold)' }}>
                  {h.number}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold flex items-center gap-1.5" style={{ color: 'var(--color-text)' }}>
                    <Trophy size={12} style={{ color: 'var(--color-gold-dark)' }} />
                    {h.prize_type === 'first_prize' && '\u0E23\u0E32\u0E07\u0E27\u0E31\u0E25\u0E17\u0E35\u0E48 1'}
                    {h.prize_type === 'three_front' && '\u0E40\u0E25\u0E02\u0E2B\u0E19\u0E49\u0E32 3 \u0E15\u0E31\u0E27'}
                    {h.prize_type === 'three_last' && '\u0E40\u0E25\u0E02\u0E17\u0E49\u0E32\u0E22 3 \u0E15\u0E31\u0E27'}
                    {h.prize_type === 'two_last' && '\u0E40\u0E25\u0E02\u0E17\u0E49\u0E32\u0E22 2 \u0E15\u0E31\u0E27'}
                  </div>
                  <div className="text-[10px]" style={{ color: 'var(--color-text-soft)' }}>
                    {'\u0E07\u0E27\u0E14 '}{formatDate(h.draw_date)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm font-bold" style={{ color: 'var(--color-gold-dark)' }}>
                    {h.prize_amount.toLocaleString()}
                  </div>
                  <div className="text-[9px]" style={{ color: 'var(--color-text-soft)' }}>{'\u0E1A\u0E32\u0E17'}</div>
                </div>
              </div>
            ))}
            {numberHits.length > 5 && (
              <div className="text-center text-[10px] mt-1" style={{ color: 'var(--color-text-soft)' }}>
                {'\u0E41\u0E25\u0E30\u0E2D\u0E35\u0E01 '}{numberHits.length - 5}{' \u0E04\u0E23\u0E31\u0E49\u0E07'}
              </div>
            )}
          </div>
        </>
      )}

      {/* Saved Numbers */}
      <SectionHeader title={'\u0E40\u0E25\u0E02\u0E40\u0E01\u0E47\u0E1A \u2B50'} icon={'\u{1F340}'} />
      <div className="mx-4 mb-4 card-amulet p-4">
        {savedNumbers && savedNumbers.length > 0 ? (
          <>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px]" style={{ color: 'var(--color-text-soft)' }}>
                {savedNumbers.length}{'/5 \u0E17\u0E35\u0E48\u0E40\u0E01\u0E47\u0E1A'}
              </span>
              {savedNumbers.some(n => n.expire_at) && (
                <span className="text-[10px]" style={{ color: 'var(--color-text-soft)' }}>
                  {'\u23F3 \u0E21\u0E35\u0E40\u0E25\u0E02\u0E43\u0E01\u0E25\u0E49\u0E25\u0E1A'}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {savedNumbers.map(item => {
                const daysLeft = item.expire_at
                  ? Math.max(0, Math.ceil((new Date(item.expire_at).getTime() - Date.now()) / (24 * 60 * 60 * 1000)))
                  : null
                const expiring = daysLeft !== null
                return (
                  <div key={item.id} className="flex flex-col items-center gap-0.5">
                    <div className="flex items-center gap-1.5 pl-3 pr-2 py-1.5 rounded-xl"
                         style={{
                           background: expiring ? 'var(--color-surface-2)' : 'var(--gradient-gold)',
                           boxShadow: expiring ? 'none' : '0 2px 8px rgba(184,148,90,0.3)',
                           border: expiring ? '1px dashed var(--color-text-soft)' : 'none',
                           opacity: expiring ? 0.85 : 1,
                         }}>
                      <span className="font-mono font-bold text-base"
                            style={{ color: expiring ? 'var(--color-text-muted)' : '#fff' }}>
                        {item.number}
                      </span>
                      <button
                        onClick={() => handleUnsave(item.number)}
                        className="w-5 h-5 rounded-full flex items-center justify-center transition-transform active:scale-90"
                        style={{ background: expiring ? 'var(--color-surface-3)' : 'rgba(255,255,255,0.25)' }}>
                        <X size={10} color={expiring ? 'var(--color-text-muted)' : '#fff'} strokeWidth={3} />
                      </button>
                    </div>
                    {daysLeft !== null && (
                      <span className="text-[9px] font-medium" style={{ color: daysLeft <= 1 ? 'var(--color-danger)' : 'var(--color-text-soft)' }}>
                        {daysLeft === 0 ? '\u0E25\u0E1A\u0E27\u0E31\u0E19\u0E19\u0E35\u0E49' : `\u0E25\u0E1A\u0E43\u0E19 ${daysLeft} \u0E27\u0E31\u0E19`}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
            <p className="text-[10px] mt-3 text-center" style={{ color: 'var(--color-text-soft)' }}>
              {'\u0E40\u0E25\u0E02\u0E17\u0E35\u0E48\u0E44\u0E21\u0E48\u0E16\u0E39\u0E01\u0E07\u0E27\u0E14 \u2192 \u0E25\u0E1A\u0E2D\u0E31\u0E15\u0E42\u0E19\u0E21\u0E31\u0E15\u0E34 3 \u0E27\u0E31\u0E19\u0E2B\u0E25\u0E31\u0E07\u0E2D\u0E2D\u0E01\u0E1C\u0E25 (\u0E16\u0E39\u0E01\u0E41\u0E25\u0E49\u0E27 = \u0E40\u0E01\u0E47\u0E1A\u0E16\u0E32\u0E27\u0E23)'}
            </p>
          </>
        ) : (
          <div className="text-center py-3">
            <p className="text-xs" style={{ color: 'var(--color-text-soft)' }}>
              {'\u0E22\u0E31\u0E07\u0E44\u0E21\u0E48\u0E21\u0E35\u0E40\u0E25\u0E02\u0E40\u0E01\u0E47\u0E1A \u2014 \u0E01\u0E14 \u2B50 \u0E17\u0E35\u0E48\u0E40\u0E25\u0E02\u0E21\u0E07\u0E04\u0E25\u0E43\u0E19\u0E2B\u0E19\u0E49\u0E32\u0E17\u0E33\u0E19\u0E32\u0E22\u0E1D\u0E31\u0E19 (\u0E2A\u0E39\u0E07\u0E2A\u0E38\u0E14 5 \u0E40\u0E25\u0E02)'}
            </p>
          </div>
        )}
      </div>

      {/* Recent Dreams */}
      <SectionHeader title={'\u0E1D\u0E31\u0E19\u0E25\u0E48\u0E32\u0E2A\u0E38\u0E14'} icon={'\u{1F52E}'} />
      <div className="mx-4 mb-4 card-amulet p-3 flex flex-col gap-2">
        {stats?.recent_dreams && stats.recent_dreams.length > 0 ? (
          stats.recent_dreams.slice(0, 5).map(d => (
            <button key={d.id} onClick={() => setSelectedDream(d)}
                    className="flex items-center gap-3 p-2 rounded-xl text-left transition-transform active:scale-[0.98]"
                    style={{ background: 'var(--color-surface-2)' }}>
              <div className="text-2xl">{ANIMAL_LABELS[d.animal]?.split(' ')[0] ?? '\u2728'}</div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate" style={{ color: 'var(--color-text)' }}>
                  {ANIMAL_LABELS[d.animal] ?? d.animal}
                </div>
                <div className="text-[10px]" style={{ color: 'var(--color-text-soft)' }}>
                  {formatDate(d.created_at)} • {formatTime(d.created_at)}
                </div>
              </div>
              <div className="flex gap-1">
                {(d.lucky_numbers ?? []).slice(0, 3).map((n, i) => (
                  <span key={i} className="w-7 h-7 rounded-lg flex items-center justify-center text-[11px] font-bold"
                        style={{ background: 'var(--gradient-gold)', color: '#fff' }}>
                    {n.toString().padStart(2, '0')}
                  </span>
                ))}
              </div>
              <ChevronRight size={14} style={{ color: 'var(--color-text-soft)' }} />
            </button>
          ))
        ) : (
          <EmptyHint text={'\u0E22\u0E31\u0E07\u0E44\u0E21\u0E48\u0E21\u0E35\u0E1B\u0E23\u0E30\u0E27\u0E31\u0E15\u0E34\u0E01\u0E32\u0E23\u0E17\u0E33\u0E19\u0E32\u0E22\u0E1D\u0E31\u0E19'} />
        )}
      </div>

      {/* Admin Panel */}
      {(profile?.role === 'admin' || profile?.role === 'super_admin') && (
        <div className="mx-4 mb-4">
          <button onClick={() => navigate('/admin/dashboard')}
                  className="w-full p-4 flex items-center gap-3 text-left transition-transform active:scale-[0.98] rounded-2xl"
                  style={{ background: 'linear-gradient(135deg,#1a1d18 0%,#2e3329 100%)', color: '#f0ead8', boxShadow: '0 4px 12px rgba(26,29,24,0.25)' }}>
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                 style={{ background: 'rgba(184,148,90,0.2)' }}>
              <Shield size={18} style={{ color: '#d4b884' }} />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-sm flex items-center gap-2">
                Admin Panel
                {profile?.role === 'super_admin' && (
                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded"
                        style={{ background: '#d4b884', color: '#1a1d18' }}>SUPER</span>
                )}
              </div>
              <div className="text-[11px] opacity-70">
                {'\u0E08\u0E31\u0E14\u0E01\u0E32\u0E23\u0E1C\u0E39\u0E49\u0E43\u0E0A\u0E49 \u0E18\u0E38\u0E23\u0E01\u0E23\u0E23\u0E21 \u0E41\u0E25\u0E30\u0E23\u0E30\u0E1A\u0E1A'}
              </div>
            </div>
            <ChevronRight size={16} style={{ color: '#d4b884' }} />
          </button>
        </div>
      )}

      {/* Payment History */}
      <div className="mx-4 mb-4">
        <button onClick={() => navigate('/profile/transactions')}
                className="w-full p-4 card-amulet flex items-center gap-3 text-left transition-transform active:scale-[0.98]">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--color-surface-2)' }}>
            <CreditCard size={18} style={{ color: 'var(--color-gold-dark)' }} />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-sm" style={{ color: 'var(--color-text)' }}>
              {'\u0E1B\u0E23\u0E30\u0E27\u0E31\u0E15\u0E34\u0E01\u0E32\u0E23\u0E0A\u0E33\u0E23\u0E30\u0E40\u0E07\u0E34\u0E19'}
            </div>
            <div className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>
              {'\u0E14\u0E39\u0E23\u0E32\u0E22\u0E01\u0E32\u0E23\u0E18\u0E38\u0E23\u0E01\u0E23\u0E23\u0E21\u0E17\u0E31\u0E49\u0E07\u0E2B\u0E21\u0E14 ('}{stats?.total_payments ?? 0}{' \u0E23\u0E32\u0E22\u0E01\u0E32\u0E23)'}
            </div>
          </div>
          <ChevronRight size={16} style={{ color: 'var(--color-text-soft)' }} />
        </button>
      </div>

      {/* Terms */}
      <div className="mx-4 mb-4">
        <button onClick={() => navigate('/terms')}
                className="w-full p-4 card-amulet flex items-center gap-3 text-left transition-transform active:scale-[0.98]">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'var(--color-surface-2)' }}>
            <ScrollText size={18} style={{ color: 'var(--color-gold-dark)' }} />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-sm" style={{ color: 'var(--color-text)' }}>
              {'\u0E02\u0E49\u0E2D\u0E01\u0E33\u0E2B\u0E19\u0E14\u0E01\u0E32\u0E23\u0E43\u0E0A\u0E49\u0E07\u0E32\u0E19'}
            </div>
            <div className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>
              {'T&C • \u0E19\u0E42\u0E22\u0E1A\u0E32\u0E22\u0E04\u0E27\u0E32\u0E21\u0E40\u0E1B\u0E47\u0E19\u0E2A\u0E48\u0E27\u0E19\u0E15\u0E31\u0E27 • \u0E02\u0E49\u0E2D\u0E08\u0E33\u0E01\u0E31\u0E14\u0E04\u0E27\u0E32\u0E21\u0E23\u0E31\u0E1A\u0E1C\u0E34\u0E14\u0E0A\u0E2D\u0E1A'}
            </div>
          </div>
          <ChevronRight size={16} style={{ color: 'var(--color-text-soft)' }} />
        </button>
      </div>

      {/* Referral \u2014 LIVE Day 22 */}
      {lineUid && <ReferralSection lineUid={lineUid} />}

      {/* Coming Soon */}
      <SectionHeader title={'\u0E01\u0E33\u0E25\u0E31\u0E07\u0E08\u0E30\u0E21\u0E32'} icon={'\u2728'} />
      <div className="mx-4 grid grid-cols-2 gap-2">
        <ComingSoonCard icon={'\u{1F3C6}'} label={'Achievement'} desc={'\u0E20\u0E32\u0E23\u0E01\u0E34\u0E08\u0E2A\u0E30\u0E2A\u0E21'} />
        <ComingSoonCard icon={'\u{1F3B4}'} label={'Story Telling'} desc={'\u0E41\u0E1A\u0E1A\u0E17\u0E14\u0E2A\u0E2D\u0E1A\u0E14\u0E27\u0E07'} />
      </div>

      <div className="flex flex-col items-center gap-2 mt-6 mb-2 px-4">
        <button
          onClick={() => {
            localStorage.removeItem('__app_v')
            window.location.replace('/?r=' + Date.now())
          }}
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all active:scale-95"
          style={{ background: 'var(--color-surface-2)', color: 'var(--color-text-muted)', border: '1px solid var(--color-surface-3)' }}
        >
          <span style={{ fontSize: '1rem', lineHeight: 1 }}>↻</span>
          รีโหลดแอป
        </button>
        <p className="text-[10px]" style={{ color: 'var(--color-text-soft)' }}>
          Amulet Digit • v1.0 MVP
        </p>
      </div>
    </div>
  )
}